import { eachDay, format } from 'date-fns'
import { get } from 'lodash'
import { createSelector } from 'reselect'
import { IStore } from '../index'
import { getNewPrices } from '../price/selectors/price'

const mapPTypeToPriceType = (pType: string) => {
  if (pType && pType.includes('BedBooking') && !pType.includes('BookingDependent')) return 'bedbooking'
  if (pType && (pType.includes('Booking-') || pType.includes('BookingDependent'))) return 'booking'
  if (pType && pType.includes('Airbnb')) return 'airbnb'
  return 'bedbooking'
}

const RESTRICTION_KEYS = [
  'restriction_min_stay',
  'restriction_max_stay',
  'restriction_min_stay_thru',
  'restriction_max_stay_thru',
  'restriction_exact_stay',
  'restriction_close_at_arrival',
  'restriction_close_at_departure',
  'restriction_min_before_arrival',
  'restriction_max_before_arrival',
]

function hasAnyRestrictionSet(record: Record<string, unknown>): boolean {
  return RESTRICTION_KEYS.some(key => {
    const v = get(record, key)
    return v !== undefined && v !== null && v !== '' && Number(v) !== 0
  })
}

function hasRestrictionsInSelectedRange(
  periods: Array<{ start?: Date; end?: Date }>,
  roomId: number | undefined,
  type: string | undefined,
  newPrices: Array<{
    id_room?: string
    restrictions?: Record<
      string,
      Array<{
        restriction_day_from?: string
        restriction_day_to?: string
        [k: string]: unknown
      }>
    >
  }>,
): boolean {
  if (!roomId || !type || !newPrices || !periods.length) return false
  const priceType = mapPTypeToPriceType(type)
  const roomPrices = newPrices.find(p => String(p.id_room) === String(roomId))
  const restrictionsList =
    roomPrices && roomPrices.restrictions && roomPrices.restrictions[priceType]
  if (!Array.isArray(restrictionsList) || restrictionsList.length === 0)
    return false
  for (const period of periods) {
    const start = period.start ? new Date(period.start) : null
    const end = period.end ? new Date(period.end) : null
    if (!start || !end) continue
    const days = eachDay(start, end)
    for (const day of days) {
      const dateStr = format(day, 'YYYY-MM-DD')
      const hasMatch = restrictionsList.some(r => {
        const fromRaw = r.restriction_day_from
        const toRaw = r.restriction_day_to
        if (!fromRaw || !toRaw) return false
        // API may return ISO datetime (e.g. 2026-02-23T00:00:00.000Z) – compare date part only
        const from =
          typeof fromRaw === 'string'
            ? fromRaw.substring(0, 10)
            : String(fromRaw).substring(0, 10)
        const to =
          typeof toRaw === 'string'
            ? toRaw.substring(0, 10)
            : String(toRaw).substring(0, 10)
        if (
          dateStr >= from &&
          dateStr <= to &&
          hasAnyRestrictionSet(r as Record<string, unknown>)
        )
          return true
        return false
      })
      if (hasMatch) return true
    }
  }
  return false
}

function totalDaysInPeriods(
  periods: Array<{ start?: Date; end?: Date }>,
): number {
  let total = 0
  for (const period of periods) {
    const start = period.start ? new Date(period.start) : null
    const end = period.end ? new Date(period.end) : null
    if (start && end) total += eachDay(start, end).length
  }
  return total
}

export const getPriceEditModalOpen = (state: IStore) => state.priceEdit.isOpen
export const getPriceEditModalRoomId = (state: IStore) => state.priceEdit.roomId
export const getPriceEditModalStartDate = (state: IStore) =>
  state.priceEdit.startDate
export const getPriceEditModalPeriods = (state: IStore) =>
  state.priceEdit.data && state.priceEdit.data.periods
    ? state.priceEdit.data.periods
    : []
export const getPriceEditModalRooms = (state: IStore) =>
  state.priceEdit.data && state.priceEdit.data.rooms
    ? state.priceEdit.data.rooms
    : []
export const getPriceEditModalPrices = (state: IStore) =>
  state.priceEdit.data && state.priceEdit.data.prices
    ? state.priceEdit.data.prices
    : {}
export const getPriceEditModalRestrictions = (state: IStore) =>
  state.priceEdit.data && state.priceEdit.data.restrictions
    ? state.priceEdit.data.restrictions
    : {}
export const getPriceEditModalData = (state: IStore) =>
  state.priceEdit.data ? state.priceEdit.data : {}
export const getPriceEditModalType = (state: IStore) => state.priceEdit.type
export const getPriceEditModalInitialValues = (state: IStore) =>
  state.priceEdit.initial
export const getPriceEditModalFields = (state: IStore) => state.priceEdit.fields
export const getPriceEditModalErrors = (state: IStore) => state.priceEdit.errors
export const getPriceEditModalLoading = (state: IStore) =>
  state.priceEdit.loading
export const getPriceEditModalKeepRestrictions = (state: IStore) =>
  state.priceEdit.keepRestrictions ?? undefined

export const getPriceEditModalRestrictionsBanner = createSelector(
  [
    getPriceEditModalPeriods,
    getPriceEditModalRoomId,
    getPriceEditModalType,
    (state: IStore) => getNewPrices(state) || [],
  ],
  (periods, roomId, type, newPrices) => {
    const moreThanOneDay = totalDaysInPeriods(periods) > 1
    const hasRestrictionsInRangeFromApi = hasRestrictionsInSelectedRange(
      periods,
      roomId,
      type,
      newPrices,
    )
    return {
      showBanner: moreThanOneDay && hasRestrictionsInRangeFromApi,
      moreThanOneDay,
      hasRestrictionsInRange: hasRestrictionsInRangeFromApi,
    }
  },
)
